import React from 'react';
import Icon from '../../../components/AppIcon';

const TrainingRecommendations = ({ recommendations }) => {
  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'high':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          border: 'border-error/30',
          badge: 'bg-error text-error-foreground'
        };
      case 'medium':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          border: 'border-warning/30',
          badge: 'bg-warning text-warning-foreground'
        };
      case 'low':
        return {
          bg: 'bg-accent/10',
          text: 'text-accent',
          border: 'border-accent/30',
          badge: 'bg-accent text-accent-foreground'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          border: 'border-border',
          badge: 'bg-muted text-muted-foreground'
        };
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      compliance: 'Shield',
      payroll: 'DollarSign',
      leave: 'Calendar',
      workforce: 'Users',
      employee: 'UserCheck',
      contract: 'FileText',
      approval: 'CheckCircle',
      audit: 'Eye',
      calendar: 'CalendarDays',
      policy: 'Book'
    };
    return icons?.[category] || 'BookOpen';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-1">
              Training Needs Assessment
            </h3>
            <p className="caption text-xs md:text-sm text-muted-foreground">
              Personalized learning paths based on low-engagement areas
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full">
            <Icon name="GraduationCap" size={16} color="currentColor" />
            <span className="caption text-xs font-medium">
              {recommendations?.length} recommendations
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {recommendations?.map((rec, index) => {
          const styles = getPriorityStyles(rec?.priority);
          return (
            <div 
              key={index}
              className={`p-4 rounded-md border ${styles?.border} ${styles?.bg} transition-smooth hover:shadow-sm`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="bg-primary/10 text-primary p-2 rounded-md">
                    <Icon name={getCategoryIcon(rec?.featureCategory)} size={20} color="currentColor" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground mb-1">
                        {rec?.featureName}
                      </h4>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`caption text-xs px-2 py-0.5 rounded-full ${styles?.badge}`}>
                          {rec?.priority} priority
                        </span>
                        <span className="caption text-xs text-muted-foreground capitalize">
                          {rec?.featureCategory}
                        </span>
                        {rec?.department && (
                          <span className="caption text-xs text-muted-foreground">
                            • {rec?.department}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="caption text-sm text-foreground mb-3">
                    {rec?.recommendationText}
                  </p>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <Icon name="TrendingDown" size={12} color="var(--color-error)" />
                      <span className="data-text text-error font-medium">
                        {rec?.lowAdoptionPercentage}% adoption
                      </span>
                    </div>
                    {rec?.affectedUsersCount > 0 && (
                      <div className="flex items-center gap-1">
                        <Icon name="Users" size={12} color="var(--color-muted-foreground)" />
                        <span className="data-text text-muted-foreground">
                          {rec?.affectedUsersCount} users affected
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {recommendations?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3 opacity-50" />
          <p className="caption text-sm text-success font-medium">Excellent feature adoption</p>
          <p className="caption text-xs text-muted-foreground mt-1">No training recommendations at this time</p>
        </div>
      )}
    </div>
  );
};

export default TrainingRecommendations;
